// src/api/client.ts
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://hrlama.ru/api';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Перехватчик запроса: добавляем Bearer-токен из localStorage
api.interceptors.request.use(
  (config) => {
    const sessionToken = sessionStorage.getItem('token');
    const localToken = localStorage.getItem('token');
    const token = sessionToken || localToken;
    console.log(`📤 ${config.method?.toUpperCase()} ${config.url}`);
    if (token) {
      const cleanToken = token.replace(/^"|"$/g, '').trim();
      config.headers.Authorization = `Bearer ${cleanToken}`;
      console.log(`🔐 Токен добавлен: ${cleanToken.substring(0, 20)}...`);
    } else {
      console.warn(`⚠️ Токен отсутствует в localStorage`);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// Перехватчик ответа: обработка ошибок + автообновление токена
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isElearning = originalRequest?.url?.includes('/elearning');

    // Не пытаемся рефрешить, если сам refresh/login вернул 401
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/refresh')
                        || originalRequest?.url?.includes('/auth/login');
    if (error.response?.status === 401 && !originalRequest._retry && !isElearning && !isAuthEndpoint) {
      if (isRefreshing) {
        // ★ КРИТИЧНО: помечаем ДО повторного запроса, чтобы не зациклиться
        originalRequest._retry = true;
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
      if (!refreshToken) {
        isRefreshing = false;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        if (window.location.pathname !== '/login') window.location.replace('/login');
        return Promise.reject(error);
      }

      try {
        const res = await api.post('/auth/refresh', { refreshToken });
        const newAccessToken = res.data.token;
        localStorage.setItem('token', newAccessToken);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        try {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('user');
        } catch (e) {}
        // ★ replace — не добавляет в history
        window.location.replace('/login');
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', error.config?.url, error.response?.status, error.response?.data);
    }
    return Promise.reject(error);
  }
);

// Существующая функция
export const getFeeStats = async () => {
  const res = await api.get('/vacancies/fee-stats');
  return res.data;
};

// ========== E-LEARNING API ==========

export const getTests = async (params?: { active?: boolean; limit?: number; category?: string }) => {
  const res = await api.get('/elearning/tests', { params });
  return res.data;
};

export const getTestById = async (id: string) => {
  const res = await api.get(`/elearning/tests/${id}`);
  return res.data;
};

export const createTest = async (data: any) => {
  const res = await api.post('/elearning/tests', data);
  return res.data;
};

export const updateTest = async (id: string, data: any) => {
  const res = await api.put(`/elearning/tests/${id}`, data);
  return res.data;
};

export const deleteTest = async (id: string) => {
  const res = await api.delete(`/elearning/tests/${id}`);
  return res.data;
};

// Soft delete and trash
export const softDeleteTest = async (id: string) => {
  const res = await api.delete(`/elearning/tests/${id}/soft`);
  return res.data;
};

export const getDeletedTests = async () => {
  const res = await api.get('/elearning/tests/deleted');
  return res.data;
};

export const restoreTest = async (id: string) => {
  const res = await api.post(`/elearning/tests/${id}/restore`);
  return res.data;
};

export const permanentDeleteTest = async (id: string) => {
  const res = await api.delete(`/elearning/tests/${id}/permanent`);
  return res.data;
};

export const clearTrash = async () => {
  const res = await api.post('/elearning/tests/trash/clear');
  return res.data;
};

// Questions
export const getQuestions = async (testId: string) => {
  const res = await api.get('/elearning/questions', { params: { testId } });
  return res.data;
};

export const createQuestion = async (data: any) => {
  const res = await api.post('/elearning/questions', data);
  return res.data;
};

export const updateQuestion = async (id: string, data: any) => {
  const res = await api.put(`/elearning/questions/${id}`, data);
  return res.data;
};

export const deleteQuestion = async (id: string) => {
  const res = await api.delete(`/elearning/questions/${id}`);
  return res.data;
};

// Results
export const submitResult = async (data: any) => {
  const res = await api.post('/elearning/results', data);
  return res.data;
};

// Rating
export const getRating = async (limit?: number) => {
  const res = await api.get('/elearning/rating', { params: { limit } });
  return res.data;
};

export const getTopRating = async (limit: number = 1) => {
  const res = await api.get('/elearning/rating/top', { params: { limit } });
  return res.data;
};

export const getTopCompanyRating = async () => {
  const res = await api.get('/elearning/rating/top-company');
  return res.data;
};

// Benefits
export const getBenefits = async () => {
  const res = await api.get('/elearning/benefits');
  return res.data;
};

export const getBenefitById = async (id: string) => {
  const res = await api.get(`/elearning/benefits/${id}`);
  return res.data;
};

export const createBenefit = async (data: any) => {
  const res = await api.post('/elearning/benefits', data);
  return res.data;
};

export const updateBenefit = async (id: string, data: any) => {
  const res = await api.put(`/elearning/benefits/${id}`, data);
  return res.data;
};

export const deleteBenefit = async (id: string) => {
  const res = await api.delete(`/elearning/benefits/${id}`);
  return res.data;
};

export const claimBenefit = async (benefitId: string) => {
  const res = await api.post(`/elearning/benefits/${benefitId}/claim`);
  return res.data;
};

// Analytics
export const getAnalytics = async () => {
  const res = await api.get('/elearning/analytics/personal');
  return res.data;
};

export const getPersonalAnalytics = async () => {
  const res = await api.get('/elearning/analytics/personal');
  return res.data;
};

// ---------- ИЗБРАННОЕ ----------
export const toggleFavorite = async (testId: string) => {
  const res = await api.post(`/elearning/favorites/${testId}`);
  return res.data;
};

export const getFavoriteTests = async () => {
  const res = await api.get('/elearning/favorites');
  return res.data;
};

export const checkFavorite = async (testId: string) => {
  const res = await api.get(`/elearning/favorites/${testId}/check`);
  return res.data;
};

// ═══════════════════════════════════════════════════════════
//  ФИЧА C: Массовое назначение
// ═══════════════════════════════════════════════════════════
export const bulkAssign = async (params: {
  testId: string;
  deadline: string;
  notes?: string;
  filter: { type: 'department' | 'role'; value: string };
}) => {
  const res = await api.post('/elearning/assignments/bulk', params);
  return res.data;
};

export const getAssignmentFilters = async () => {
  const res = await api.get('/elearning/assignments/filters');
  return res.data;
};

// ═══════════════════════════════════════════════════════════
//  ФИЧА D: Теги
// ═══════════════════════════════════════════════════════════
export const getAllTags = async () => {
  const res = await api.get('/elearning/tags');
  return res.data;
};

// ═══════════════════════════════════════════════════════════
//  ФИЧА G: Верификация сертификата (ПУБЛИЧНЫЙ)
// ═══════════════════════════════════════════════════════════
export const verifyCertificate = async (number: string) => {
  const res = await fetch(`/api/elearning/certificates/verify/${encodeURIComponent(number)}`);
  return res.json();
};

// ═══════════════════════════════════════════════════════════
//  ФИЧА H: Аудит
// ═══════════════════════════════════════════════════════════
export const getElearningAudit = async (params?: {
  entity?: string;
  action?: string;
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
}) => {
  const res = await api.get('/elearning/audit', { params });
  return res.data;
};

// ═══════════════════════════════════════════════════════════
//  ФИЧА M: Экспорт тестов в Excel
// ═══════════════════════════════════════════════════════════
export const exportTestsExcel = async (ids?: string[]) => {
  const params = ids && ids.length > 0 ? `?ids=${ids.join(',')}` : '';
  return fetch(`/api/elearning/tests/export.xlsx${params}`, {
    headers: {
      Authorization: 'Bearer ' + (localStorage.getItem('token') || localStorage.getItem('accessToken') || localStorage.getItem('authToken') || ''),
    },
  });
};

// ═══════════════════════════════════════════════════════════
//  ФИЧА Q: Импорт вопросов из Excel
// ═══════════════════════════════════════════════════════════
export const importQuestionsExcel = async (testId: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post(`/elearning/tests/${testId}/import-excel`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

// ═══════════════════════════════════════════════════════════
//  ФИЧА E: Библиотека вопросов
// ═══════════════════════════════════════════════════════════
export const getQuestionsLibrary = async (params?: {
  search?: string;
  type?: string;
  excludeTestId?: string;
  limit?: number;
}) => {
  const res = await api.get('/elearning/questions/library', { params });
  return res.data;
};

export const importQuestionsFromLibrary = async (testId: string, questionIds: string[]) => {
  const res = await api.post(`/elearning/tests/${testId}/questions/from-library`, { questionIds });
  return res.data;
};

// ═══════════════════════════════════════════════════════════
//  ФИЧА N: Календарь назначений
// ═══════════════════════════════════════════════════════════
export const getAssignmentsCalendar = async (params?: { dateFrom?: string; dateTo?: string }) => {
  const res = await api.get('/elearning/assignments/calendar', { params });
  return res.data;
};

// ═══════════════════════════════════════════════════════════
//  ФИЧА F: Сравнительная аналитика
// ═══════════════════════════════════════════════════════════
export const getComparativeAnalytics = async (period: string = 'month') => {
  const res = await api.get('/elearning/analytics/comparative', { params: { period } });
  return res.data;
};

// ═══════════════════════════════════════════════════════════
//  ФИЧА R: KPI
// ═══════════════════════════════════════════════════════════
export const getKPIs = async () => {
  const res = await api.get('/elearning/kpi');
  return res.data;
};

export const getKPIProgress = async () => {
  const res = await api.get('/elearning/kpi/progress');
  return res.data;
};

// ═══════════════════════════════════════════════════════════
//  ФИЧА S: Комментарии к тестам
// ═══════════════════════════════════════════════════════════
export const getTestComments = async (testId: string) => {
  const res = await api.get(`/elearning/tests/${testId}/comments`);
  return res.data;
};

export const addTestComment = async (testId: string, text: string, parentId?: string) => {
  const res = await api.post(`/elearning/tests/${testId}/comments`, { text, parentId });
  return res.data;
};

export const deleteTestComment = async (id: string) => {
  const res = await api.delete(`/elearning/comments/${id}`);
  return res.data;
};

// ═══════════════════════════════════════════════════════════
//  ФИЧА T: Learning Paths
// ═══════════════════════════════════════════════════════════
export const getElearningPaths = async (params?: { active?: boolean }) => {
  const res = await api.get('/elearning/paths', { params });
  return res.data;
};

export const getElearningPathById = async (id: string) => {
  const res = await api.get(`/elearning/paths/${id}`);
  return res.data;
};

export const createElearningPath = async (data: any) => {
  const res = await api.post('/elearning/paths', data);
  return res.data;
};

export const updateElearningPath = async (id: string, data: any) => {
  const res = await api.put(`/elearning/paths/${id}`, data);
  return res.data;
};

export const deleteElearningPath = async (id: string) => {
  const res = await api.delete(`/elearning/paths/${id}`);
  return res.data;
};

export const assignElearningPath = async (id: string, userIds: string[]) => {
  const res = await api.post(`/elearning/paths/${id}/assign`, { userIds });
  return res.data;
};

export const getMyElearningPaths = async () => {
  const res = await api.get('/elearning/paths/my');
  return res.data;
};

export const getPathProgress = async (id: string) => {
  const res = await api.get(`/elearning/paths/${id}/progress`);
  return res.data;
};

export const startElearningPath = async (id: string) => {
  const res = await api.post(`/elearning/paths/${id}/start`);
  return res.data;
};

// ═══════════════════════════════════════════════════════════
//  ФИЧА Y: ELearning Auto Rules
// ═══════════════════════════════════════════════════════════
export const getElearningAutoRules = async () => {
  const res = await api.get('/elearning/auto-rules');
  return res.data;
};

export const createElearningAutoRule = async (data: any) => {
  const res = await api.post('/elearning/auto-rules', data);
  return res.data;
};

export const updateElearningAutoRule = async (id: string, data: any) => {
  const res = await api.put(`/elearning/auto-rules/${id}`, data);
  return res.data;
};

export const deleteElearningAutoRule = async (id: string) => {
  const res = await api.delete(`/elearning/auto-rules/${id}`);
  return res.data;
};

export const getElearningAutoRulesMeta = async () => {
  const res = await api.get('/elearning/auto-rules/meta/available');
  return res.data;
};

// ═══════════════════════════════════════════════════════════
//  GAMIFICATION (Achievements, Badges, Leaderboard)
// ═══════════════════════════════════════════════════════════
export const getGamificationMe = async () => {
  const res = await api.get('/elearning/gamification/me');
  return res.data;
};

export const getGamificationLeaderboard = async (period: string = 'all', limit: number = 20) => {
  const res = await api.get('/elearning/gamification/leaderboard', { params: { period, limit } });
  return res.data;
};

export const getGamificationBadges = async () => {
  const res = await api.get('/elearning/gamification/badges');
  return res.data;
};

export const getGamificationAchievements = async () => {
  const res = await api.get('/elearning/gamification/achievements');
  return res.data;
};

// ═══════════════════════════════════════════════════════════
//  ASSIGNMENTS (назначения)
// ═══════════════════════════════════════════════════════════
export const createAssignment = async (
  testId: string,
  userIds: string[],
  deadline: string,
  notes?: string,
) => {
  const res = await api.post('/elearning/assignments', {
    testId, userIds, deadline, notes,
  });
  return res.data;
};

export const getMyAssignments = async () => {
  const res = await api.get('/elearning/assignments/my');
  return res.data;
};

export const getAssignments = async (params?: {
  status?: string;
  userId?: string;
  testId?: string;
}) => {
  const res = await api.get('/elearning/assignments', { params });
  return res.data;
};

export const cancelAssignment = async (id: string) => {
  const res = await api.delete(`/elearning/assignments/${id}`);
  return res.data;
};

export const markAssignmentInProgress = async (testId: string) => {
  const res = await api.post(`/elearning/assignments/check-my-test/${testId}`);
  return res.data;
};

// ═══════════════════════════════════════════════════════════
//  ONBOARDING
// ═══════════════════════════════════════════════════════════
export const assignOnboardingTests = async (userId: string) => {
  const res = await api.post(`/elearning/onboarding/assign/${userId}`);
  return res.data;
};

// ═══════════════════════════════════════════════════════════
//  MANDATORY (обязательные тесты)
// ═══════════════════════════════════════════════════════════
export const getMandatoryStatus = async () => {
  const res = await api.get('/elearning/mandatory/status');
  return res.data;
};

export const getMandatoryOverview = async () => {
  const res = await api.get('/elearning/mandatory/all');
  return res.data;
};

// ═══════════════════════════════════════════════════════════
//  HR DASHBOARD
// ═══════════════════════════════════════════════════════════
export const getHROverview = async () => {
  const res = await api.get('/elearning/hr/overview');
  return res.data;
};

// ═══════════════════════════════════════════════════════════
//  KPI CRUD
// ═══════════════════════════════════════════════════════════
export const createKPI = async (data: any) => {
  const res = await api.post('/elearning/kpi', data);
  return res.data;
};

export const updateKPI = async (id: string, data: any) => {
  const res = await api.put(`/elearning/kpi/${id}`, data);
  return res.data;
};

export const deleteKPI = async (id: string) => {
  const res = await api.delete(`/elearning/kpi/${id}`);
  return res.data;
};

// ═══════════════════════════════════════════════════════════
//  USERS (для назначения)
// ═══════════════════════════════════════════════════════════
export const getCompanyUsers = async () => {
  const res = await api.get('/users/company-users');
  return res.data;
};

export default api;
